import {
  type ListReviewQueueQuery,
  REVIEW_REASON_LABELS,
  REVIEW_STATUS_LABELS,
  type ReviewQueueItem,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, cn, Spinner } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  reviewKeys,
  reviewQueueQuery,
  reviewStatsQuery,
  fileCertainDocuments,
  setAsideDuplicates,
} from '../../shared/api/review-queries.js';

import { SuppliersToTeach } from './suppliers-to-teach.js';
/**
 * The review queue.
 *
 * Built for a morning's work rather than a screenshot: virtualised rows, a
 * debounced search, and an ordering that puts the most urgent document first
 * without letting anything rot at the bottom of the list.
 */

export function ReviewQueuePage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';

  const [filters, setFilters] = useState<Partial<ListReviewQueueQuery>>({ sort: 'priority' });
  const [search, setSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => {
        const { query: _dropped, ...rest } = current;
        return search.trim() === '' ? rest : { ...rest, query: search.trim() };
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const queue = useQuery(reviewQueueQuery(organizationId, filters));
  const queryClient = useQueryClient();
  const stats = useQuery(reviewStatsQuery(organizationId));

  // The same bill photographed twice should never have cost a decision. New
  // uploads no longer queue one; this clears the ones already waiting, by the
  // same strict test, in a single action.
  const [sweepResult, setSweepResult] = useState<string | null>(null);
  // Everything the engine has no question about: read cleanly, a supplier this
  // company knows, an account they already use. Filed as drafts, so the books
  // fill in and a person still posts them.
  const fileCertain = useMutation({
    mutationFn: () => fileCertainDocuments(organizationId),
    onSuccess: ({ filed, failed, firstError }) => {
      // Three different outcomes, said differently. A run that files nothing
      // because there was nothing to file is good news; a run that files
      // nothing because every attempt failed is not, and they read the same
      // until the failures are counted out loud.
      setSweepResult(
        failed > 0 && filed === 0
          ? `None could be filed. ${String(failed)} attempt${failed === 1 ? '' : 's'} failed: ${firstError ?? 'no reason given'}`
          : filed === 0
            ? 'Nothing here can be filed without a question yet. Give a supplier a default account and the ones from them will go through.'
            : `${String(filed)} filed as drafts, in the books and categorised, waiting to be posted.${
                failed > 0 ? ` ${String(failed)} could not be filed and stayed in the queue.` : ''
              }`,
      );
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all(organizationId) });
    },
    onError: (caught) =>
      setSweepResult(caught instanceof Error ? caught.message : 'Those could not be filed.'),
  });

  const sweep = useMutation({
    mutationFn: () => setAsideDuplicates(organizationId),
    onSuccess: ({ setAside }) => {
      setSweepResult(
        setAside === 0
          ? 'Nothing here is a certain duplicate, so nothing was touched.'
          : `${String(setAside)} duplicate${setAside === 1 ? '' : 's'} set aside. Each one is archived, not deleted.`,
      );
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all(organizationId) });
    },
    onError: (caught) =>
      setSweepResult(
        caught instanceof Error ? caught.message : 'Those duplicates could not be set aside.',
      ),
  });

  const items = queue.data?.items ?? [];

  // React's compiler lint flags TanStack Virtual as unmemoizable; it measures
  // during render by design. Accepted, the alternative is rendering every row.
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 64,
    overscan: 8,
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-content">Review</h1>
        <p className="mt-1 text-sm text-content-muted">
          The AI has read these. Confirm what it got right and fix what it did not.
        </p>
      </div>

      <SuppliersToTeach organizationId={organizationId} />

      {stats.data === undefined ? null : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="To review" value={String(stats.data.open)} />
          <Stat label="In progress" value={String(stats.data.inProgress)} />
          <Stat label="Done today" value={String(stats.data.resolvedToday)} />
          <Stat
            label="Typical review"
            value={
              stats.data.medianSecondsToDecide === null
                ? '-'
                : `${Math.round(stats.data.medianSecondsToDecide)}s`
            }
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by file name"
          aria-label="Search the review queue"
          className="min-w-48 flex-1 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
        />
        <select
          aria-label="Sort"
          value={filters.sort ?? 'priority'}
          onChange={(event) =>
            setFilters((current) => ({
              ...current,
              sort: event.target.value as ListReviewQueueQuery['sort'],
            }))
          }
          className="rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
        >
          <option value="priority">Most urgent first</option>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="status">By status</option>
        </select>
      </div>

      {queue.isError ? (
        <Alert tone="danger" title="Could not load the queue">
          {queue.error instanceof Error ? queue.error.message : 'Please try again.'}
        </Alert>
      ) : null}

      {queue.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner className="size-6 text-brand-600" label="Loading the queue" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardBody className="py-12 text-center">
            <p className="text-sm text-content-muted">
              Nothing waiting. Every document that has been read is reviewed.
            </p>
          </CardBody>
        </Card>
      ) : (
        <Card>
          {sweepResult === null ? null : (
            <div className="px-5 pt-4">
              <Alert tone="info">{sweepResult}</Alert>
            </div>
          )}
          <CardHeader
            title={`${items.length} waiting`}
            description="Press J and K on a review to move between documents without returning here."
            actions={
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => fileCertain.mutate()}
                  isLoading={fileCertain.isPending}
                >
                  File the certain ones
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => sweep.mutate()}
                  isLoading={sweep.isPending}
                >
                  Set aside duplicates
                </Button>
              </div>
            }
          />
          <CardBody className="p-0">
            <div ref={scrollRef} className="max-h-[34rem] overflow-y-auto">
              <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const item = items[virtualRow.index];
                  if (item === undefined) return null;
                  return (
                    <div
                      key={item.id}
                      ref={virtualizer.measureElement}
                      data-index={virtualRow.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <QueueRow item={item} />
                    </div>
                  );
                })}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {stats.data !== undefined && stats.data.mostCorrectedFields.length > 0 ? (
        <Card>
          <CardHeader
            title="Most corrected fields"
            description="Where the model is weakest, the first thing worth improving in the prompt."
          />
          <CardBody>
            <ul className="flex flex-col gap-1 text-sm">
              {stats.data.mostCorrectedFields.map((field) => (
                <li key={field.fieldPath} className="flex justify-between">
                  <span className="text-content">{field.label}</span>
                  <span className="tabular text-content-muted">{field.corrections}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface px-3 py-2">
      <p className="text-xs text-content-muted">{label}</p>
      <p className="tabular text-lg font-semibold text-content">{value}</p>
    </div>
  );
}

function QueueRow({ item }: { item: ReviewQueueItem }) {
  return (
    <Link
      to="/review/$itemId"
      params={{ itemId: item.id }}
      className="flex items-center gap-3 border-b border-border px-3 py-2.5 hover:bg-surface-sunken"
    >
      {/* Urgency as a bar rather than a number: a reviewer needs to see which
          rows to start with, not to compare 34 against 41. */}
      <span
        aria-hidden="true"
        className={cn(
          'h-8 w-1 shrink-0 rounded-full',
          item.priority <= 25 ? 'bg-danger' : item.priority <= 45 ? 'bg-warning' : 'bg-border',
        )}
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-content">{item.title}</p>
        <p className="truncate text-xs text-content-muted">
          {REVIEW_REASON_LABELS[item.reason]}
          {item.detail === null ? '' : ` · ${item.detail}`}
        </p>
      </div>

      {item.hasDraft ? (
        <span className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
          Draft saved
        </span>
      ) : null}

      <span className="shrink-0 text-xs text-content-subtle">
        {REVIEW_STATUS_LABELS[item.status]}
      </span>
    </Link>
  );
}
